/**
 * Routine schedules — one implementation for deciding whether a schedule is
 * valid and for computing when it fires. The routine contract
 * (`schemas.ts`) refuses what {@link scheduleProblem} rejects, and the routine
 * engine computes fire times with {@link nextFire} / {@link previousFire}, so
 * a schedule that was accepted is one the engine can run.
 *
 * A schedule is a 5-field cron expression (minute hour day-of-month month
 * day-of-week) or a shorthand such as `@daily`. The parser also takes a
 * leading seconds field; that is refused, because every fire starts an agent
 * run and the documented contract (tool descriptions, the dashboard picker)
 * is minute granularity.
 */
import { CronExpressionParser } from 'cron-parser';
import { isKnownTimeZone } from './intl.js';

const CRON_FIELDS = 5;

/** Why `cron` (in `timeZone`, if given) can't be scheduled, or null when it can. */
export function scheduleProblem(cron: string, timeZone?: string): string | null {
  const expr = cron.trim();
  if (timeZone !== undefined && !isKnownTimeZone(timeZone)) {
    return `unknown time zone "${timeZone}" (use an IANA name such as America/New_York)`;
  }
  if (!expr.startsWith('@') && expr.split(/\s+/).length !== CRON_FIELDS) {
    return `"${expr}" must have 5 fields (minute hour day-of-month month day-of-week) or be a shorthand like @daily`;
  }
  try {
    CronExpressionParser.parse(expr, timeZone ? { tz: timeZone } : {});
    return null;
  } catch (err) {
    return `"${expr}" is not a valid schedule: ${err instanceof Error ? err.message : String(err)}`;
  }
}

/** The next time `cron` fires after `from`. Throws on a schedule {@link scheduleProblem} rejects. */
export function nextFire(cron: string, timeZone?: string, from: Date = new Date()): Date {
  return CronExpressionParser.parse(cron.trim(), timeZone ? { tz: timeZone, currentDate: from } : { currentDate: from })
    .next()
    .toDate();
}

/** The most recent time `cron` fired before `from`, or null for a schedule that can't be parsed. */
export function previousFire(cron: string, timeZone?: string, from: Date = new Date()): Date | null {
  try {
    return CronExpressionParser.parse(
      cron.trim(),
      timeZone ? { tz: timeZone, currentDate: from } : { currentDate: from },
    )
      .prev()
      .toDate();
  } catch {
    return null;
  }
}
